import { Component, Vue } from 'vue-property-decorator'
import {
  ApiResponseStudyResultDetails,
  ApiResponseStudyResultLastMinuteCheck,
  ApiResponseStudyResultResolve,
} from '@/models/apiResponses/studyResultDetails'
import { UserRoles } from '@/const/UserRoles'
import { StudyResultDetailImage } from '@/models/studyResultDetailModel'

@Component
export default class StudyResultDetailsApi extends Vue {
  /**
   * 管理ページに表示するテスト結果詳細情報を取得する
   * @param studentId 生徒ID
   * @param paperTestId 紙テスト結果ID
   * @param role ユーザー権限
   * @returns テスト結果詳細情報
   */
  protected async getStudyResultDetails(
    studentId: number,
    paperTestId: number,
    role: UserRoles
  ): Promise<ApiResponseStudyResultDetails> {
    const { data }: { data: ApiResponseStudyResultDetails } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/study_results/paper_test_detail`, {
        params: { studentId: studentId, paperTestId: paperTestId },
      })
      .catch(() => ({ data: null }))

    return data
  }

  /**
   * 管理ページに表示するテスト直前チェック実施結果取得
   * @param studentId 生徒ID
   * @param curriculumSUnitId 小項目ID
   * @param role ユーザー権限
   * @returns テスト直前チェック実施結果
   */
  protected async getStudyResultLastMinuteCheck(
    studentId: number,
    curriculumSUnitId: number,
    role: UserRoles
  ): Promise<ApiResponseStudyResultLastMinuteCheck> {
    const { data }: { data: ApiResponseStudyResultLastMinuteCheck } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/study_results/last_minute_check`, {
        params: { studentId: studentId, curriculumSUnitId: curriculumSUnitId },
      })
      .catch(() => ({ data: null }))

    return data
  }

  /**
   * 管理ページに表示するテスト解きなおし実施結果取得
   * @param studentId 生徒ID
   * @param curriculumSUnitId 小項目ID
   * @param role ユーザー権限
   * @returns テスト解きなおし実施結果
   */
  protected async getStudyResultResolve(
    studentId: number,
    curriculumSUnitId: number,
    role: UserRoles
  ): Promise<ApiResponseStudyResultResolve> {
    const { data }: { data: ApiResponseStudyResultResolve } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/study_results/study_result_resolve`, {
        params: { studentId: studentId, curriculumSUnitId: curriculumSUnitId },
      })
      .catch(() => ({ data: null }))

    return data
  }

  /**
   * 生徒別結果資料ダウンロード
   * @param year 実施年度
   * @param originalCurriculumId オリジナルカリキュラムID
   * @param role ユーザー権限
   * @returns [生徒別結果資料PDFデータ, ヘッダー情報]
   */
  protected async downloadStudyResultPdfStudents(
    year: number,
    originalCurriculumId: number,
    role: UserRoles
  ): Promise<[Blob, { [headerKey: string]: string }]> {
    const res = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/study_results/download_pdf_students`, {
      params: { year: year, originalCurriculumId: originalCurriculumId },
      responseType: 'blob',
      dataType: 'binary',
    })

    return [res.data, res.headers]
  }

  /**
   * 結果帳票ダウンロード
   * @param year 実施年度
   * @param originalCurriculumId オリジナルカリキュラムID
   * @param role ユーザー権限
   * @returns [結果帳票PDFデータ, ヘッダー情報]
   */
  protected async downloadStudyResultPdf(
    year: number,
    originalCurriculumId: number,
    role: UserRoles
  ): Promise<[Blob, { [headerKey: string]: string }]> {
    const res = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/study_results/download_result_pdf`, {
      params: { year: year, originalCurriculumId: originalCurriculumId },
      responseType: 'blob',
      dataType: 'binary',
    })

    return [res.data, res.headers]
  }

  /**
   * 結果Excelダウンロード
   * @param year 実施年度
   * @param originalCurriculumId オリジナルカリキュラムID
   * @param role ユーザー権限
   * @returns [結果Excelデータ, ヘッダー情報]
   */
  protected async downloadStudyResultExcel(
    year: number,
    originalCurriculumId: number,
    role: UserRoles
  ): Promise<[Blob, { [headerKey: string]: string }]> {
    const res = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/study_results/download_result_excel`, {
      params: { year: year, originalCurriculumId: originalCurriculumId },
      responseType: 'blob',
      dataType: 'binary',
    })

    return [res.data, res.headers]
  }

  /**
   * 生徒毎の結果資料ダウンロード
   * @param year 実施年度
   * @param originalCurriculumId オリジナルカリキュラムID
   * @param studentId 生徒ID
   * @param role ユーザー権限
   * @returns [生徒毎の結果資料PDFデータ, ヘッダー情報]
   */
  protected async downloadStudyResultPdfPersonal(
    year: number,
    originalCurriculumId: number,
    studentId: number,
    role: UserRoles
  ): Promise<[Blob, { [headerKey: string]: string }]> {
    const res = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/study_results/download_pdf_personal`, {
      params: { year: year, originalCurriculumId: originalCurriculumId, studentId: studentId },
      responseType: 'blob',
      dataType: 'binary',
    })

    return [res.data, res.headers]
  }

  /**
   * 紙テスト結果取り込みバッチ実行
   */
  protected async execPaperTestResultsBatch(): Promise<void> {
    await Vue.prototype.$http.httpWithToken.get('/v2/admin/study_results/exec_paper_test_results_batch')
  }

  /**
   * 管理ページに表示するテスト直前チェック実施結果取得
   * @param studentId 生徒ID
   * @param curriculumSUnitId 小項目ID
   * @param role ユーザー権限
   * @param category カテゴリー
   * @returns テスト直前チェック実施結果
   */
  protected async getStudyResultImageUrl(
    studentId: number,
    curriculumSUnitId: number,
    role: UserRoles,
    category: number
  ): Promise<StudyResultDetailImage[]> {
    const { data }: { data: StudyResultDetailImage[] } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/study_results/study_result_capture_url`, {
        params: { studentId: studentId, curriculumSUnitId: curriculumSUnitId, category: category },
      })
      .catch(() => ({ data: null }))

    return data
  }
}
